import { useState, useEffect, useRef, useCallback } from 'react'
import { useAuth } from '../../contexts/AuthContext'

interface Message {
  role: 'user' | 'assistant'
  content: string
}

export function CoachScottWidget() {
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [open])

  const sendMessage = useCallback(async () => {
    const text = input.trim()
    if (!text || loading) return

    const userMessage: Message = { role: 'user', content: text }
    const nextMessages = [...messages, userMessage]
    setMessages(nextMessages)
    setInput('')
    setLoading(true)
    setError(null)

    try {
      // Get auth token (anonymous sessions get null token -function handles unauthenticated)
      let token: string | null = null
      if (user) {
        token = await user.getIdToken()
      }

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      }
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const res = await fetch('/api/coachScott', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          messages: nextMessages,
          sessionId: getSessionId(),
          page: window.location.href,
        }),
      })

      if (!res.ok) {
        if (res.status === 429) {
          let rateLimitMessage =
            'Coach Scott is taking a breather, try again in a few minutes.'
          try {
            const data = (await res.json()) as { message?: string }
            if (typeof data.message === 'string' && data.message.length > 0) {
              rateLimitMessage = data.message
            }
          } catch {
            // Response body unparseable — fall through with default.
          }
          setMessages((prev) => [
            ...prev,
            { role: 'assistant', content: rateLimitMessage },
          ])
          return
        }
        if (res.status === 413) {
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              content: 'That message is too long. Try a shorter one.',
            },
          ])
          return
        }
        setError('Something went wrong. Please try again.')
        return
      }

      const data = await res.json() as { message: string }
      setMessages((prev) => [...prev, { role: 'assistant', content: data.message }])
    } catch {
      setError('Connection error. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [input, loading, messages, user])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  return (
    <>
      {/* Chat bubble -always visible when widget is closed */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-gold shadow-xl flex items-center justify-center hover:opacity-90 transition-opacity duration-150"
          aria-label="Open chat with Coach Scott"
        >
          <ChatIcon />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className="fixed z-40 flex flex-col bg-white shadow-2xl
            bottom-0 right-0 left-0 max-h-[60vh] rounded-t-2xl
            sm:left-auto sm:bottom-6 sm:right-6 sm:w-[380px] sm:h-[480px] sm:max-h-[480px] sm:rounded-lg"
        >
          {/* Header */}
          <div className="flex items-center justify-between bg-navy px-4 py-3 rounded-t-2xl sm:rounded-t-lg flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-gold flex items-center justify-center flex-shrink-0">
                <span className="font-heading text-navy text-sm font-bold">CS</span>
              </div>
              <div>
                <p className="font-body text-white text-sm font-semibold leading-tight">Coach Scott</p>
                <p className="font-body text-white/60 text-xs leading-tight">AI Assistant</p>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-white/60 hover:text-white transition-colors w-9 h-9 flex items-center justify-center"
              aria-label="Close chat"
            >
              <CloseIcon />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-center px-4">
                <div className="w-14 h-14 rounded-full bg-gold/10 flex items-center justify-center mb-4">
                  <ChatIcon className="text-gold" />
                </div>
                <p className="font-heading text-navy text-xl mb-2">Ask Coach Scott</p>
                <p className="font-body text-sm text-gray-500 leading-relaxed">
                  Get real answers about your horse-rider challenges. No fluff.
                </p>
              </div>
            )}

            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] px-3 py-2.5 rounded-lg font-body text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-navy text-white rounded-br-none'
                      : 'bg-white text-gray-800 shadow-sm rounded-bl-none border border-gray-100'
                  }`}
                >
                  {msg.content}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-white shadow-sm border border-gray-100 rounded-lg rounded-bl-none px-4 py-3 flex gap-1">
                  <span className="w-2 h-2 bg-gold rounded-full animate-bounce [animation-delay:0ms]" />
                  <span className="w-2 h-2 bg-gold rounded-full animate-bounce [animation-delay:150ms]" />
                  <span className="w-2 h-2 bg-gold rounded-full animate-bounce [animation-delay:300ms]" />
                </div>
              </div>
            )}

            {error && (
              <div className="text-center">
                <p className="font-body text-xs text-red-500 bg-red-50 rounded px-3 py-2 inline-block">
                  {error}
                </p>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-gray-200 p-3 bg-white sm:rounded-b-lg flex-shrink-0">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask a question..."
                className="flex-1 font-body text-base text-gray-800 placeholder-gray-400 border border-gray-200 rounded px-3 py-2.5 min-h-[44px] focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold"
                maxLength={10000}
                aria-label="Message to Coach Scott"
              />
              <button
                onClick={sendMessage}
                disabled={loading || !input.trim()}
                className="bg-gold text-navy w-11 min-h-[44px] flex items-center justify-center rounded disabled:opacity-40 hover:opacity-90 transition-opacity flex-shrink-0"
                aria-label="Send message"
              >
                <SendIcon />
              </button>
            </div>
            <p className="font-body text-xs text-gray-400 mt-2 text-center">
              AI trained on Scott's methodology. Not a human.
            </p>
          </div>
        </div>
      )}
    </>
  )
}

// --- Helpers ---

function getSessionId(): string {
  const key = 'coach-scott-session'
  let id = sessionStorage.getItem(key)
  if (!id) {
    id = Math.random().toString(36).slice(2) + Date.now().toString(36)
    sessionStorage.setItem(key, id)
  }
  return id
}

// --- Icons ---

function ChatIcon({ className = 'text-navy' }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={`w-6 h-6 ${className}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
      />
    </svg>
  )
}

function CloseIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  )
}

function SendIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
      />
    </svg>
  )
}
